import React, { useState, memo } from 'react';
import { useVote } from '@/hooks/useVote';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronUp } from 'lucide-react';

interface InlineVoteButtonProps {
  postId: string;
  initialScore: number;
  initialVoted?: boolean;
  disabled?: boolean;
}

export const InlineVoteButton = memo(function InlineVoteButton({
  postId,
  initialScore,
  initialVoted = false,
  disabled = false,
}: InlineVoteButtonProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { voted, score, toggleVote, isLoading } = useVote({
    postId,
    initialVoted,
    initialScore,
  });
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }

    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    toggleVote();
  };

  const ariaLabel = disabled
    ? 'Cannot vote on your own post'
    : voted
      ? `Remove vote. Current votes: ${score}`
      : `Upvote. Current votes: ${score}`;

  return (
    <button
      onClick={handleClick}
      disabled={isLoading || disabled}
      className={cn(
        'inline-flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 min-w-[48px]',
        voted
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        (isLoading || disabled) && 'opacity-50 cursor-not-allowed',
        disabled && 'pointer-events-none',
        isAnimating && 'scale-105'
      )}
      title={disabled ? 'Cannot vote on your own post' : (voted ? 'Remove vote' : 'Upvote')}
      aria-label={ariaLabel}
      aria-pressed={voted}
    >
      <ChevronUp
        className={cn(
          'h-5 w-5 transition-all duration-200 -mb-1',
          voted && 'text-primary',
          isAnimating && voted && '-translate-y-0.5'
        )}
        fill={voted ? 'currentColor' : 'none'}
        strokeWidth={voted ? 2.5 : 2}
        aria-hidden="true"
      />
      <span
        className={cn(
          'font-semibold transition-all duration-200',
          isAnimating && 'scale-110'
        )}
      >
        {score}
      </span>
    </button>
  );
});
